/**
 * socialPipeline.ts — the Social pipeline contracts: ideas, drafts written from ideas, second-opinion reviewers,
 * learned rules and the "This week" strip. It follows the Ads pipeline step for step (two models, a second opinion,
 * three-state rules) so a founder learns one system.
 *
 * The AI Worker gets only what a model may use and saves nothing. The CMS Worker re-checks every post with the same
 * rulebook (socialAi.ts) before saving, and a person approves every post.
 */
import { z } from "zod";
import { aiModelChoiceSchema, aiProviderSchema } from "./ai.ts";
import { AD_HOOK_TACTICS, adConceptTagsSchema, adProblemSchema } from "./adsDrafts.ts";
import { adProductIdSchema } from "./adsProducts.ts";
import { adReviewFindingSchema, otherModelItemSchema } from "./adsAi.ts";
import { adFeedbackTagSchema } from "./adsFeedback.ts";
import { learnedStatusSchema } from "./adsLearned.ts";
import { socialNetworkSchema } from "./socialAi.ts";

export const SOCIAL_IDEA_SOURCES = ["results", "competitor", "article", "winning_post", "brief"] as const;
export const socialIdeaSourceSchema = z.enum(SOCIAL_IDEA_SOURCES);
export type SocialIdeaSource = z.infer<typeof socialIdeaSourceSchema>;
export const SOCIAL_IDEA_SOURCE_LABEL: Record<SocialIdeaSource, string> = {
  results: "Our own results",
  competitor: "Competitor ad (reference only)",
  article: "Recent article",
  winning_post: "Winning post",
  brief: "Brief",
};

export const SOCIAL_AI_PIPELINE_LIMITS = { maxIdeas: 6, maxBrief: 1500, minCompetitorImportance: 4 } as const;

// ---- Ideas ----

export const savedSocialIdeaSchema = z.object({
  id: z.string(),
  product: adProductIdSchema,
  weekStart: z.string(),
  title: z.string(),
  angle: z.string(),
  evidence: z.string(),
  evidenceLink: z.string().nullable(),
  /** 1 to 5, or null when the idea was written by a person. */
  score: z.number().int().min(1).max(5).nullable(),
  scoreReason: z.string(),
  hookTactic: z.enum(AD_HOOK_TACTICS).nullable(),
  /** "Grok", "Claude" or "Both"; null for an idea a person wrote. */
  suggestedBy: z.string().nullable(),
  /** The id of the model that wrote it. */
  writtenBy: z.string().nullable(),
  source: socialIdeaSourceSchema,
  sourceRef: z.string().nullable(),
  status: z.enum(["open", "used", "dismissed"]),
  createdAt: z.string(),
});
export type SavedSocialIdea = z.infer<typeof savedSocialIdeaSchema>;
export const savedSocialIdeaListSchema = z.object({ items: z.array(savedSocialIdeaSchema) });

/** What the model may read to find ideas. Competitor wording is reference only and is never to be copied. */
export const socialIdeaSourcesSchema = z.object({
  results: z.array(z.string().max(300)).max(12).default([]),
  competitorAds: z.array(z.object({ company: z.string().max(120), headline: z.string().max(300), text: z.string().max(800) })).max(12).default([]),
  articles: z.array(z.object({ title: z.string().max(300), summary: z.string().max(600) })).max(10).default([]),
  /** A winning post, a brief or a single competitor ad / article the person started from. */
  startingPoint: z.object({ kind: socialIdeaSourceSchema, text: z.string().trim().min(1).max(2500) }).optional(),
});
export type SocialIdeaSources = z.infer<typeof socialIdeaSourcesSchema>;

export const socialIdeasRequestSchema = z.object({
  model: aiModelChoiceSchema,
  product: adProductIdSchema,
  count: z.number().int().min(1).max(SOCIAL_AI_PIPELINE_LIMITS.maxIdeas).default(4),
  sources: socialIdeaSourcesSchema.default({}),
  /** Confirmed and inferred rules, already rendered. */
  feedback: z.string().max(3000).default(""),
  facts: z.array(z.string().max(500)).max(60).default([]),
  /** The first model's ideas when this is the second pass. */
  otherModel: z.object({ name: z.enum(["Grok", "Claude"]), items: z.array(otherModelItemSchema).max(SOCIAL_AI_PIPELINE_LIMITS.maxIdeas) }).optional(),
});
export type SocialIdeasRequest = z.infer<typeof socialIdeasRequestSchema>;

export const socialIdeasResponseSchema = z.object({
  items: z
    .array(
      z.object({
        title: z.string().trim().min(3).max(160),
        angle: z.string().max(300),
        evidence: z.string().max(2000),
        evidenceLink: z.string().max(500).nullable(),
        score: z.number().int().min(1).max(5),
        scoreReason: z.string().max(1000),
        hookTactic: z.enum(AD_HOOK_TACTICS),
        writtenBy: z.string().min(1).max(100),
      })
    )
    .min(1),
  dropped: z.number().int().min(0),
  usage: z.object({ provider: aiProviderSchema, model: z.string(), inputTokens: z.number().int(), outputTokens: z.number().int() }),
});
export type SocialIdeasResponse = z.infer<typeof socialIdeasResponseSchema>;

/** Browser -> CMS Worker. `origin` starts from one thing instead of the week's sources. */
export const generateSocialIdeasBodySchema = z.object({
  siteId: z.string().min(1).max(100),
  product: adProductIdSchema,
  count: z.number().int().min(1).max(SOCIAL_AI_PIPELINE_LIMITS.maxIdeas).default(4),
  origin: z.object({ kind: socialIdeaSourceSchema, ref: z.string().max(200).optional(), text: z.string().trim().min(1).max(SOCIAL_AI_PIPELINE_LIMITS.maxBrief) }).optional(),
});
export type GenerateSocialIdeasBody = z.infer<typeof generateSocialIdeasBodySchema>;
export const generateSocialIdeasResultSchema = z.object({ created: z.array(z.string()), models: z.string(), dropped: z.number().int(), costUsd: z.number().nullable() });
export type GenerateSocialIdeasResult = z.infer<typeof generateSocialIdeasResultSchema>;

// ---- Posts written from ideas ----

export const generateSocialPostsBodySchema = z.object({
  siteId: z.string().min(1).max(100),
  product: adProductIdSchema,
  ideaIds: z.array(z.string().min(1).max(100)).min(1).max(5),
  channelIds: z.array(z.string().min(1).max(200)).min(1).max(6),
});
export type GenerateSocialPostsBody = z.infer<typeof generateSocialPostsBodySchema>;
export const generateSocialPostsResultSchema = z.object({ created: z.array(z.string()), skipped: z.array(z.object({ ideaId: z.string(), channelId: z.string(), reason: z.string() })), costUsd: z.number().nullable() });
export type GenerateSocialPostsResult = z.infer<typeof generateSocialPostsResultSchema>;

/** The fields a social post carries beyond its words (shown in Posts and in Review). */
export const socialPostExtraSchema = z.object({
  product: adProductIdSchema.nullable(),
  ideaId: z.string().nullable(),
  suggestedBy: z.string().nullable(),
  writtenBy: z.string().nullable(),
  tags: adConceptTagsSchema.partial().nullable(),
  /** Length and rule checks plus the reviewers' notes, in the same shape ads use. */
  problems: z.array(adProblemSchema),
  sceneImageId: z.string().nullable(),
  suggestedTime: z.string().nullable(),
  isReference: z.boolean(),
  referenceSource: z.enum(["auto", "manual"]).nullable(),
  verdictTags: z.array(adFeedbackTagSchema),
});
export type SocialPostExtra = z.infer<typeof socialPostExtraSchema>;

// ---- Second opinion on one post (POST /v1/social/review) ----

export const socialReviewRequestSchema = z.object({
  model: aiModelChoiceSchema,
  product: adProductIdSchema,
  network: socialNetworkSchema,
  text: z.string().min(1).max(70_000),
  facts: z.array(z.string().max(500)).max(60),
  messagingRules: z.array(z.string().max(500)).max(40),
  competitorHooks: z.array(z.string().max(300)).max(60).default([]),
  /** Edited reviewer prompts. Only the reviewer's own instruction; the security and output rules are always added by code. */
  prompts: z.object({ product_accuracy: z.string().max(4000).optional(), messaging: z.string().max(4000).optional() }).optional(),
});
export type SocialReviewRequest = z.infer<typeof socialReviewRequestSchema>;
export const socialReviewResponseSchema = z.object({
  findings: z.array(adReviewFindingSchema).max(20),
  usage: z.object({ provider: aiProviderSchema, model: z.string(), inputTokens: z.number().int(), outputTokens: z.number().int() }),
});
export type SocialReviewResponse = z.infer<typeof socialReviewResponseSchema>;

// ---- Learned rules (same three states as ads) ----

export const socialLearnedRuleSchema = z.object({
  id: z.string(),
  product: adProductIdSchema,
  weekStart: z.string(),
  text: z.string(),
  sourceTags: z.array(adFeedbackTagSchema),
  status: learnedStatusSchema,
  verifiedByName: z.string().nullable(),
  verifiedAt: z.string().nullable(),
  createdAt: z.string(),
  usedInLastRun: z.boolean(),
});
export type SocialLearnedRule = z.infer<typeof socialLearnedRuleSchema>;
export const socialLearnedRulesResponseSchema = z.object({ items: z.array(socialLearnedRuleSchema), lastRunAt: z.string().nullable() });

// ---- This week (the strip) ----

export const THIS_WEEK_STATUSES = ["done", "waiting", "running", "failed", "not_yet"] as const;
export type ThisWeekStatus = (typeof THIS_WEEK_STATUSES)[number];
export const THIS_WEEK_STATUS_LABEL: Record<ThisWeekStatus, string> = { done: "Done", waiting: "Waiting for you", running: "Running", failed: "Failed", not_yet: "Not yet" };

export const thisWeekCellSchema = z.object({
  id: z.string(),
  label: z.string(),
  count: z.number().int().nullable(),
  unit: z.string(),
  status: z.enum(THIS_WEEK_STATUSES),
  /** When the step last ran (null when it has never run). */
  lastRunAt: z.string().nullable(),
  /** "Grok + Claude", "Claude", … or null when no model is involved or none is recorded. */
  models: z.string().nullable(),
});
export type ThisWeekCell = z.infer<typeof thisWeekCellSchema>;

export const costByProviderSchema = z.object({
  provider: z.enum(["claude", "grok", "google", "semrush"]),
  costUsd: z.number().nullable(),
  units: z.number().nullable(),
});
export type CostByProvider = z.infer<typeof costByProviderSchema>;

export const socialThisWeekSchema = z.object({
  weekStart: z.string(),
  cells: z.array(thisWeekCellSchema),
  lastRun: z.object({ at: z.string(), status: z.enum(["running", "done", "failed"]), model: z.string().nullable(), error: z.string().nullable(), rules: z.array(z.string()) }).nullable(),
  cost: z.array(costByProviderSchema),
});
export type SocialThisWeek = z.infer<typeof socialThisWeekSchema>;

/** Cell status from plain counts, the same way for Ads and Social. */
export function cellStatus(input: { count: number; waitingForYou?: boolean; running?: boolean; failed?: boolean }): ThisWeekStatus {
  if (input.running) return "running";
  if (input.failed && input.count === 0) return "failed";
  if (input.waitingForYou) return "waiting";
  return input.count > 0 ? "done" : "not_yet";
}

/** "Grok + Claude" from the stored run model ("grok+anthropic" or "claude-…"), or null when nothing is recorded. */
export function modelsLabel(model: string | null | undefined): string | null {
  if (!model) return null;
  const m = model.toLowerCase();
  const parts = [m.includes("grok") || m.includes("xai") ? "Grok" : null, m.includes("anthropic") || m.includes("claude") ? "Claude" : null, m.includes("openai") || m.includes("gpt") ? "OpenAI" : null, m.includes("google") || m.includes("gemini") ? "Gemini" : null].filter((x): x is string => !!x);
  return parts.length > 0 ? parts.join(" + ") : model;
}
